import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Icon, tokens } from '@/design';
import { LayoutNorthArrow, NORTH_ARROW_STYLES } from './LayoutNorthArrow';
import {
  MARKER_FONT_GLYPHS,
  SHAPE_MARKER_FORMS,
  ShapeMarkerArtwork,
} from './markerShapes';
import { SymbolColorPicker } from './SymbolColorPicker';
import type {
  NorthArrowMarkerLayer,
  NorthArrowStyleId,
} from './layoutTypes';

export interface ShapeMarkerLayerPanelProps {
  readonly marker: NorthArrowMarkerLayer;
  readonly styleId: NorthArrowStyleId;
  readonly sizePt: number;
  readonly onMarkerChange: (patch: Partial<NorthArrowMarkerLayer>) => void;
  readonly onStyleChange: (styleId: NorthArrowStyleId) => void;
  readonly onSizeChange: (sizePt: number) => void;
}

type OpenPicker = 'form' | 'style' | 'font' | null;

export function ShapeMarkerLayerPanel({
  marker,
  styleId,
  sizePt,
  onMarkerChange,
  onStyleChange,
  onSizeChange,
}: ShapeMarkerLayerPanelProps) {
  const [openPicker, setOpenPicker] = useState<OpenPicker>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!openPicker) return undefined;
    const closeOutside = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpenPicker(null);
    };
    const closeEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenPicker(null);
    };
    window.addEventListener('mousedown', closeOutside);
    window.addEventListener('keydown', closeEscape);
    return () => {
      window.removeEventListener('mousedown', closeOutside);
      window.removeEventListener('keydown', closeEscape);
    };
  }, [openPicker]);

  const field: CSSProperties = {
    minHeight: tokens.density.rowDense,
    width: '100%',
    boxSizing: 'border-box',
    padding: `0 ${tokens.space[2]}px`,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: tokens.color.surface[0],
    color: tokens.color.ink.primary,
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  };
  const row: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `minmax(0, 1fr) ${tokens.layout.popoverXs / 2}px`,
    alignItems: 'center',
    gap: tokens.space[2],
    fontSize: tokens.font.size.sm,
  };
  const button: CSSProperties = {
    minHeight: tokens.density.rowDense,
    padding: `0 ${tokens.space[3]}px`,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: tokens.color.surface[0],
    color: tokens.color.ink.primary,
    cursor: 'pointer',
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  };
  const picker: CSSProperties = {
    position: 'absolute',
    zIndex: tokens.zIndex.tooltip,
    top: `calc(100% + ${tokens.space[1]}px)`,
    right: 0,
    width: tokens.layout.popoverXs,
    maxHeight: tokens.size.dropdownMaxHeight,
    overflowY: 'auto',
    padding: tokens.space[2],
    border: tokens.border.thin,
    borderRadius: tokens.radius.md,
    background: tokens.color.surface[1],
    boxShadow: tokens.elevation[2],
  };
  const tile: CSSProperties = {
    minHeight: tokens.density.rowComfy,
    padding: tokens.space[1],
    border: tokens.border.thin,
    borderRadius: tokens.radius.sm,
    background: tokens.color.canvas.light,
    color: tokens.color.ink.inverse,
    cursor: 'pointer',
  };

  const sourceLabel =
    marker.source === 'style'
      ? NORTH_ARROW_STYLES.find((style) => style.id === styleId)?.label
      : marker.source === 'form'
        ? SHAPE_MARKER_FORMS.find((form) => form.id === marker.formId)?.label
        : marker.source === 'font'
          ? `Font glyph ${marker.fontGlyph}`
          : marker.fileName ?? 'File';

  return (
    <div
      ref={rootRef}
      data-testid="shape-marker-layer-panel"
      style={{ display: 'grid', gap: tokens.space[2] }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `${tokens.space[5]}px ${tokens.density.rowComfy}px minmax(0, 1fr)`,
          alignItems: 'center',
          gap: tokens.space[2],
          padding: tokens.space[1],
          border: tokens.border.thin,
          borderBottom: `${tokens.focus.ringWidth}px solid ${tokens.color.ink.muted}`,
          background: tokens.color.surface[2],
        }}
      >
        <input
          type="checkbox"
          aria-label="Shape marker layer visible"
          checked={marker.visible}
          onChange={(event) => onMarkerChange({ visible: event.target.checked })}
        />
        <span
          aria-hidden
          style={{
            display: 'block',
            width: tokens.density.rowDefault,
            height: tokens.density.rowDefault,
            padding: tokens.space[1],
            boxSizing: 'border-box',
            background: tokens.color.canvas.light,
          }}
        >
          <LayoutNorthArrow
            styleId={styleId}
            bearingDeg={0}
            color={marker.fillColor}
            marker={marker}
          />
        </span>
        <select
          aria-label="Marker layer type"
          value="shape"
          onChange={() => undefined}
          style={field}
        >
          <option value="shape">Shape marker</option>
        </select>
      </div>

      <h4
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[1],
          margin: `${tokens.space[1]}px 0 0`,
          fontSize: tokens.font.size.sm,
          fontWeight: tokens.font.weight.semibold,
        }}
      >
        <Icon name="expand_more" size={16} decorative /> Appearance
      </h4>

      <div style={{ paddingLeft: tokens.space[5], display: 'grid', gap: tokens.space[2] }}>
        <span style={{ color: tokens.color.ink.secondary, fontSize: tokens.font.size.sm }}>
          Insert shape from
        </span>

        <div style={row}>
          <span>Form</span>
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              aria-label="Choose marker form"
              aria-haspopup="dialog"
              aria-expanded={openPicker === 'form'}
              onClick={() =>
                setOpenPicker((current) => current === 'form' ? null : 'form')
              }
              style={{
                ...button,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: `0 ${tokens.space[2]}px`,
                background:
                  marker.source === 'form'
                    ? tokens.color.accent.subtle
                    : tokens.color.surface[0],
              }}
            >
              <span
                aria-hidden
                style={{
                  width: tokens.space[5],
                  height: tokens.space[5],
                  color: tokens.color.ink.primary,
                }}
              >
                <svg viewBox="0 0 64 80" width="100%" height="100%">
                  <ShapeMarkerArtwork marker={{ ...marker, source: 'form' }} />
                </svg>
              </span>
              <Icon name="expand_more" size={16} decorative />
            </button>
            {openPicker === 'form' ? (
              <div
                role="dialog"
                aria-label="Marker forms"
                data-testid="marker-form-gallery"
                style={picker}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: tokens.space[2],
                  }}
                >
                  {SHAPE_MARKER_FORMS.map((form) => (
                    <button
                      key={form.id}
                      type="button"
                      title={form.label}
                      aria-label={`Use ${form.label} marker form`}
                      aria-pressed={
                        marker.source === 'form' && marker.formId === form.id
                      }
                      onClick={() => {
                        onMarkerChange({ source: 'form', formId: form.id });
                        setOpenPicker(null);
                      }}
                      style={{
                        ...tile,
                        outline:
                          marker.source === 'form' && marker.formId === form.id
                            ? `${tokens.focus.ringWidth}px solid ${tokens.color.semantic.info}`
                            : 'none',
                      }}
                    >
                      <svg viewBox="0 0 64 80" width="100%" height="100%">
                        <ShapeMarkerArtwork
                          marker={{
                            ...marker,
                            visible: true,
                            source: 'form',
                            formId: form.id,
                          }}
                        />
                      </svg>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: tokens.space[2],
          }}
        >
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              aria-label="Choose marker from style"
              aria-expanded={openPicker === 'style'}
              onClick={() =>
                setOpenPicker((current) => current === 'style' ? null : 'style')
              }
              style={{ ...button, width: '100%' }}
            >
              Style…
            </button>
            {openPicker === 'style' ? (
              <div
                role="dialog"
                aria-label="Marker styles"
                data-testid="marker-style-gallery"
                style={{ ...picker, right: 'auto', left: 0 }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: tokens.space[2],
                  }}
                >
                  {NORTH_ARROW_STYLES.map((style) => (
                    <button
                      key={style.id}
                      type="button"
                      title={style.label}
                      aria-label={`Use ${style.label} marker style`}
                      onClick={() => {
                        onStyleChange(style.id);
                        setOpenPicker(null);
                      }}
                      style={tile}
                    >
                      <LayoutNorthArrow styleId={style.id} bearingDeg={0} />
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div style={{ position: 'relative' }}>
            <button
              type="button"
              aria-label="Choose marker from font"
              aria-expanded={openPicker === 'font'}
              onClick={() =>
                setOpenPicker((current) => current === 'font' ? null : 'font')
              }
              style={{ ...button, width: '100%' }}
            >
              Font…
            </button>
            {openPicker === 'font' ? (
              <div
                role="dialog"
                aria-label="Marker font glyphs"
                data-testid="marker-font-gallery"
                style={{ ...picker, right: 'auto', left: 0 }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: tokens.space[2],
                  }}
                >
                  {MARKER_FONT_GLYPHS.map((glyph) => (
                    <button
                      key={glyph}
                      type="button"
                      aria-label={`Use ${glyph} font marker`}
                      onClick={() => {
                        onMarkerChange({ source: 'font', fontGlyph: glyph });
                        setOpenPicker(null);
                      }}
                      style={{
                        ...tile,
                        fontFamily: tokens.font.family.ui,
                        fontSize: tokens.font.size.xl,
                      }}
                    >
                      {glyph}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <button
            type="button"
            aria-label="Choose marker from file"
            onClick={() => fileRef.current?.click()}
            style={button}
          >
            File…
          </button>
          <input
            ref={fileRef}
            type="file"
            aria-label="Marker shape file"
            accept=".svg,image/svg+xml,image/png,image/jpeg"
            style={{ display: 'none' }}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              if (!file.type.startsWith('image/')) {
                setFileError('Choose an SVG, PNG, or JPEG image.');
                return;
              }
              const reader = new FileReader();
              reader.onload = () => {
                if (typeof reader.result !== 'string') return;
                onMarkerChange({
                  source: 'file',
                  fileDataUrl: reader.result,
                  fileName: file.name,
                });
                setFileError(null);
              };
              reader.onerror = () => setFileError('The marker file could not be read.');
              reader.readAsDataURL(file);
              event.target.value = '';
            }}
          />
        </div>

        <output
          aria-live="polite"
          style={{
            minHeight: tokens.font.size.sm,
            color: fileError ? tokens.color.semantic.danger : tokens.color.ink.muted,
            fontSize: tokens.font.size.xs,
          }}
        >
          {fileError ?? `Source: ${sourceLabel}`}
        </output>

        <span style={{ color: tokens.color.ink.secondary, fontSize: tokens.font.size.sm }}>
          Shape fill symbol
        </span>
        <div style={row}>
          <span>Color</span>
          <SymbolColorPicker
            label="Shape fill color"
            value={marker.fillColor}
            onChange={(fillColor) => onMarkerChange({ fillColor })}
          />
        </div>
        <div style={row}>
          <span>Outline color</span>
          <SymbolColorPicker
            label="Shape outline color"
            value={marker.outlineColor}
            onChange={(outlineColor) => onMarkerChange({ outlineColor })}
          />
        </div>
        <label style={row}>
          <span>Outline width</span>
          <input
            aria-label="Shape outline width"
            type="number"
            min={0}
            max={48}
            step={0.25}
            value={marker.outlineWidthPt}
            onChange={(event) => {
              const next = Number(event.target.value);
              if (Number.isFinite(next) && next >= 0) {
                onMarkerChange({ outlineWidthPt: next });
              }
            }}
            style={field}
          />
        </label>
        <label style={row}>
          <span>Size</span>
          <input
            aria-label="Shape marker size in points"
            type="number"
            min={4}
            max={1200}
            step={1}
            value={Number(sizePt.toFixed(2))}
            onChange={(event) => {
              const next = Number(event.target.value);
              if (Number.isFinite(next) && next > 0) onSizeChange(next);
            }}
            style={field}
          />
        </label>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: tokens.space[2],
            fontSize: tokens.font.size.sm,
          }}
        >
          <input
            type="checkbox"
            aria-label="Respect marker frame"
            checked={marker.respectFrame}
            onChange={(event) =>
              onMarkerChange({ respectFrame: event.target.checked })
            }
          />
          Respect frame
        </label>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: tokens.space[2],
            fontSize: tokens.font.size.sm,
          }}
        >
          <input
            type="checkbox"
            aria-label="Scale marker proportionally"
            checked={marker.scaleProportionally}
            onChange={(event) =>
              onMarkerChange({ scaleProportionally: event.target.checked })
            }
          />
          Scale proportionally
        </label>
      </div>
    </div>
  );
}
